import { useState } from "preact/hooks";
import { browserDeliveryEnv } from "../app/backup";
import type { DashboardViewModel } from "../app/dashboardViewModel";
import { importAll, parseBackup, type Backup, type ImportMode } from "../storage/exportImport";
import type { Repositories } from "../storage/repositories";
import { Modal } from "./dialogs";

/**
 * Export a JSON backup through the share sheet (Save to Files / iCloud Drive / Mail on iOS), falling back to a
 * download, or import one in replace or merge mode.
 */
export function BackupDialog(props: { open: boolean; repos: Repositories; vm: DashboardViewModel; onClose: () => void }) {
  const [status, setStatus] = useState("");
  const [pending, setPending] = useState<Backup | null>(null);

  const doExport = async () => {
    const result = await props.vm.exportBackup(browserDeliveryEnv());
    setStatus(result === "cancelled" ? "Backup not saved." : result === "shared" ? "Backup shared." : "Backup downloaded.");
  };

  const pick = async (e: Event) => {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    try {
      const backup = parseBackup(await file.text());
      setPending(backup);
      setStatus(`Backup from ${backup.exportedAt}: ${backup.shots.length} shots, ${backup.beans.length} beans. Choose how to import.`);
    } catch (err) {
      setPending(null);
      setStatus(err instanceof Error ? err.message : String(err));
    }
  };

  const doImport = async (mode: ImportMode) => {
    if (!pending) return;
    const r = await importAll(props.repos, pending, mode);
    setPending(null);
    await props.vm.load();
    setStatus(`Imported: ${r.shotsAdded} shots added, ${r.shotsSkipped} skipped, ${r.beansAdded} beans, ${r.grindersAdded} grinders.`);
  };

  const close = () => { setStatus(""); setPending(null); props.onClose(); };

  return (
    <Modal open={props.open} onClose={close}>
      <h2>Backup</h2>
      <div style={{ fontSize: 13, color: "var(--ink-2)" }}>
        Your data lives only on this device. Export a backup now and then; Safari can clear site data if you clear
        history. On iPhone the share sheet lets you save it to Files or iCloud Drive, or email it to yourself.
      </div>
      <button type="button" onClick={() => void doExport()}>Export backup</button>
      <label class="field">Import backup
        <input type="file" accept="application/json,.json" onChange={(e) => void pick(e)} />
      </label>
      {pending && (
        <div class="row">
          <button class="grow danger" type="button" onClick={() => void doImport("replace")}>Replace everything</button>
          <button class="grow secondary" type="button" onClick={() => void doImport("merge")}>Merge</button>
        </div>
      )}
      {status && <div style={{ fontSize: 13 }} role="status">{status}</div>}
      <div class="actions"><button class="secondary" type="button" onClick={close}>Close</button></div>
    </Modal>
  );
}
